import React, { useEffect, useRef, useState } from 'react';
import useToken from '../../hooks/useToken';

const LOG_ENABLED = true; 

export default function ThreatModelViewerPage1() {
  const { token, refreshAccessToken } = useToken();
  const [svgContent, setSvgContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const loadedRef = useRef(false); // prevents re-running the loader

  // NEW: state for threats + selected element
  const [threatsMap, setThreatsMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [loadingThreats, setLoadingThreats] = useState(true);
  const [threatsError, setThreatsError] = useState(null);
  
  // NEW: zoom state
  const [zoom, setZoom] = useState(1);
  
  // NEW: magnifier state
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0, show: false });
  const magnifierZoom = 2.5; // Magnification level
  
  // keep a ref to the latest token so event handlers can read it
  const authRef = useRef(null);
  useEffect(() => { authRef.current = token; }, [token]);

  const logEvent = async (type, details = {}) => {
    if (!LOG_ENABLED) return;
    // ensure we have a token; refresh if needed
    let auth = authRef.current || (await refreshAccessToken().catch(() => null));
    if (!auth) return; // silently skip if not authenticated

    const payload = { event: type, timestamp: new Date().toISOString(), ...details };
    fetch('/api/threat-model/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };

  // Load once
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        const authToken = token || (await refreshAccessToken());

        // 1) get assignments
        const assignRes = await fetch('/api/assignment', {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!assignRes.ok) throw new Error('Failed to load assignments');
        const assignments = await assignRes.json();
        const assignedWebsite = assignments.find(w => w.order_index === 1);
        if (!assignedWebsite) {
          setMsg('You are not assigned to Website #1.');
          setLoading(false);
          return;
        }

        // 2) fetch DFD (returns { svg })
        const dfdRes = await fetch(`/api/website/${assignedWebsite.website_id}/threat-model`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (!dfdRes.ok) {
          setMsg('❌ The Threat Model is not available for this website.');
          setLoading(false);
          return;
        }


        const data = await dfdRes.json();
        if (!data?.svg) {
          setMsg('❌ Empty SVG payload.');
          setLoading(false);
          return;
        }

        setSvgContent(data.svg);
        setLoading(false);

        setLoadingThreats(true);
        setThreatsError(null);
        const thrRes = await fetch(
          `/api/website/${assignedWebsite.website_id}/threat-list`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (!thrRes.ok) throw new Error(`Threats HTTP ${thrRes.status}`);
        const threats = await thrRes.json(); // object keyed by SVG ids
        setThreatsMap(threats);
        setLoadingThreats(false);

        logEvent('open', { page: 'DFD_VIEWER_PAGE1', website_id: assignedWebsite.website_id });
      } catch (err) {
        console.error(err);
        setMsg('Failed to load the Threat Model.');
        setThreatsError('Failed to load threats.');
        setLoading(false);
        setLoadingThreats(false);
      }
    })();
  }, [token, refreshAccessToken]);

  // Hover/click instrumentation for injected SVG
  useEffect(() => {
    if (!svgContent) return;
    const container = document.getElementById('dfdContainer');
    console.debug('[DFD] Container found:', !!container, '| SVG children:', container?.children.length);
    if (!container) return;

    // draw.io puts <title> inside <rect> (not a <g>), so we pre-tag the
    // outermost cell <g data-cell-id> with data-semantic-id by scanning
    // all <title> elements and walking up to the cell boundary.
    const seen = new Set();
    const allTitles = container.querySelectorAll('title');
    console.debug('[DFD] Found title elements:', allTitles.length);
    let stamped = 0;
    allTitles.forEach(titleEl => {
      const id = titleEl.textContent.trim();
      if (!id || seen.has(id)) return;
      let node = titleEl.parentElement;
      while (node && node !== container) {
        if (node.tagName?.toLowerCase() === 'g' && node.getAttribute('data-cell-id')) {
          if (!node.hasAttribute('data-semantic-id')) {
            node.setAttribute('data-semantic-id', id);
            stamped++;
          }
          seen.add(id);
          break;
        }
        node = node.parentElement;
      }
    });
    console.debug('[DFD] Stamped elements:', stamped, '| Unique IDs:', seen.size, '| IDs:', [...seen]);

    // Walk up from any clicked/hovered element to find the tagged cell <g>.
    const getSemanticEl = (target) => {
      let node = target;
      while (node && node !== container) {
        const id = node.getAttribute && node.getAttribute('data-semantic-id');
        if (id) return { el: node, id };
        node = node.parentElement;
      }
      return null;
    };

    const onHover = e => {
      const found = getSemanticEl(e.target);
      if (found) {
        logEvent('hover', { element: found.id });
        found.el.classList.add('hovered');
      }
    };
    const onLeave = e => {
      const found = getSemanticEl(e.target);
      if (found) found.el.classList.remove('hovered');
    };
    const onClick = e => {
      console.debug('[DFD] Click target:', e.target.tagName, e.target.getAttribute?.('data-semantic-id'));
      const found = getSemanticEl(e.target);
      console.debug('[DFD] getSemanticEl result:', found);
      if (!found) return;
      logEvent('click', { element: found.id });
      setSelected(found.id);
      logEvent('open_threats', { component: found.id });
    };

    container.addEventListener('mouseover', onHover);
    container.addEventListener('mouseout', onLeave);
    container.addEventListener('click', onClick);
    return () => {
      container.removeEventListener('mouseover', onHover);
      container.removeEventListener('mouseout', onLeave);
      container.removeEventListener('click', onClick);
    };
  }, [svgContent, threatsMap]);

  // Visibility logging (safe/no-op if LOG_ENABLED=false)
  useEffect(() => {
    const onVis = () => logEvent('visibility_change', { visible: !document.hidden });
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const closeModal = () => {
    if (selected) {
      logEvent('close_threats', { component: selected });
      setSelected(null);
    }
  };

  const threats = selected && threatsMap[selected] ? threatsMap[selected] : [];

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setZoom(1);

  return (
    <div style={{ padding: 20, fontFamily: 'Arial, sans-serif' }}>
      <h1>Threat Model Viewer</h1>

      {/* Hover highlight */}
      <style>{`
        #dfdContainer [data-semantic-id] rect,
        #dfdContainer [data-semantic-id] path,
        #dfdContainer [data-semantic-id] ellipse,
        #dfdContainer [data-semantic-id] circle {
          transition: fill 0.15s ease, stroke 0.15s ease;
        }
        #dfdContainer [data-semantic-id].hovered rect,
        #dfdContainer [data-semantic-id].hovered path,
        #dfdContainer [data-semantic-id].hovered ellipse,
        #dfdContainer [data-semantic-id].hovered circle {
          stroke: red !important;
          fill: pink !important;
          cursor: pointer;
        }
      `}</style>

      {loading && <p>Loading Threat Model…</p>}
      {!loading && msg && <p style={{ color: '#b00020' }}>{msg}</p>}

      {!loading && !msg && (
        <>
          {/* Floating Zoom Controls - Bottom Right */}
          <div style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 12,
            background: 'white',
            border: '2px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 999
          }}>
            <button 
              onClick={handleZoomIn}
              style={{
                padding: '10px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#f0f0f0',
                minWidth: '50px'
              }}
              title="Zoom In"
            >
              +
            </button>
            <button 
              onClick={handleResetZoom}
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#e0e0e0',
                minWidth: '50px'
              }}
              title="Reset to 100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button 
              onClick={handleZoomOut}
              style={{
                padding: '10px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#f0f0f0',
                minWidth: '50px'
              }}
              title="Zoom Out"
            >
              −
            </button>
          </div>

          <div
            id="dfdContainer"
            dangerouslySetInnerHTML={{ __html: svgContent }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              // Account for zoom transform - divide by zoom to get original coordinates
              const x = (e.clientX - rect.left) / zoom;
              const y = (e.clientY - rect.top) / zoom;
              setMagnifierPos({ x, y, clientX: e.clientX, clientY: e.clientY, show: true });
            }}
            onMouseLeave={() => setMagnifierPos({ ...magnifierPos, show: false })}
            style={{ 
              display: 'inline-block',
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              transition: 'transform 0.2s ease',
              position: 'relative'
            }}
          />

          {/* Magnifier Window - Follows Mouse */}
          {magnifierPos.show && (
            <div
              style={{
                position: 'fixed',
                left: magnifierPos.clientX + 20,
                top: magnifierPos.clientY + 20,
                width: 200,
                height: 200,
                border: '1px solid #999',
                borderRadius: '8px',
                overflow: 'hidden',
                pointerEvents: 'none',
                background: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 998
              }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: svgContent }}
                style={{
                  transform: `scale(${magnifierZoom}) translate(${-magnifierPos.x + 100 / magnifierZoom}px, ${-magnifierPos.y + 100 / magnifierZoom}px)`,
                  transformOrigin: 'top left',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 20,
                  height: 20,
                  border: '2px solid red',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none'
                }}
              />
            </div>
          )}

          {/* Modal with per-component threat table */}
          {selected && (
            <div
              onClick={closeModal}
              style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#fff', padding: 20, borderRadius: 8, minWidth: 420, position: 'relative'
                }}
              >
                <button
                  onClick={closeModal}
                  style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer' }}
                >×</button>
                <h2 style={{ marginBottom: 12 }}>Threats for {selected}</h2>

                {loadingThreats ? (
                  <p>Loading threats…</p>
                ) : threatsError ? (
                  <p style={{ color: 'red' }}>Error loading threats: {threatsError}</p>
                ) : threats.length === 0 ? (
                  <p style={{ opacity: .8 }}>No threats defined for this component.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #333', padding: 8 }}>ID</th>
                        <th style={{ border: '1px solid #333', padding: 8 }}>Statement</th>
                        <th style={{ border: '1px solid #333', padding: 8 }}>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {threats.map(t => (
                        <tr key={t.id}>
                          <td
                            style={{ border: '1px solid #333', padding: 8 }}
                            onMouseEnter={() => logEvent('threat_hover', { component: selected, threatId: t.id, field: 'id' })}
                            onClick={() => logEvent('threat_click', { component: selected, threatId: t.id, field: 'id' })}
                          >{t.id}</td>
                          <td
                            style={{ border: '1px solid #333', padding: 8 }}
                            onMouseEnter={() => logEvent('threat_hover', { component: selected, threatId: t.id, field: 'statement' })}
                            onClick={() => logEvent('threat_click', { component: selected, threatId: t.id, field: 'statement' })}
                          >{t.statement}</td>
                          <td
                            style={{ border: '1px solid #333', padding: 8 }}
                            onMouseEnter={() => logEvent('threat_hover', { component: selected, threatId: t.id, field: 'category' })}
                            onClick={() => logEvent('threat_click', { component: selected, threatId: t.id, field: 'category' })}
                          >{t.category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
